/**
 * Mode Router — internal mode switching with activate/deactivate lifecycle
 * No URL router; supports ?mode= deep-linking on initial load
 */

import { store } from './store.js'
import { eventBus, EVENTS } from './event-bus.js'
import { applyPageFrame } from '../modules/page-frame/page-frame.js'

const MODES = ['create', 'gallery', 'products', 'design', 'me']

// Mode lifecycle hooks registered by feature modules
const hooks = {}

// Right panel tab defaults per mode
// gallery/products/me list no tabs in MODE_TABS (full-width, panel collapsed),
// so they need no default entry — the 'community' fallback in setMode covers them.
const MODE_TAB_DEFAULTS = {
  create: 'inspiration',
  design: 'preview',
}

// Right-panel tab GROUPS (H-2026-09-12-128): create owns the BASE group;
// design APPENDS its own tabs on top of the base instead of replacing the
// whole group. The base tabs stay mounted across a create↔design switch, so
// the panel frame and the Inspiration tab's state survive — no more "page
// jump". The News tab was removed (no content source — H-2026-09-12-128 2c).
const BASE_RP_TABS = ['inspiration', 'community']
// Every tab button present in index.html — the universe diffTabGroups diffs
// against when resolving a mode's visible set.
const ALL_RP_TABS = [...BASE_RP_TABS]

// Tabs visible per mode (fully mode-scoped — no leak across modes). The
// Gallery picker tab was removed (ADR-005 2.8.0 pin #1: the single source
// picker is the slot-click modal). why → create-frontend architecture.
// Gallery/products/me are full-width and collapse the panel (shell.css) —
// no tabs. why → 2026-07-05 UI review.
const MODE_TABS = {
  create: [...BASE_RP_TABS],
  gallery: [],
  products: [],
  design: [...BASE_RP_TABS],
  me: [],
}

/**
 * Tabs visible for a mode (pure).
 * @param {string} mode
 * @returns {string[]}
 */
export function resolveModeTabs(mode) {
  return MODE_TABS[mode] || []
}

/**
 * Diff two tab groups (pure): which tabs appear / disappear going from
 * `prevTabs` to `nextTabs`. The create↔design switch is an APPEND/REMOVE of
 * the two groups — create and design share the base set, so it never diffs.
 * @param {string[]} prevTabs
 * @param {string[]} nextTabs
 * @returns {{ show: string[], hide: string[] }}
 */
export function diffTabGroups(prevTabs, nextTabs) {
  return {
    show: nextTabs.filter((t) => !prevTabs.includes(t)),
    hide: prevTabs.filter((t) => !nextTabs.includes(t)),
  }
}

/**
 * Resolve the active tab after a group change (pure): keep the current tab
 * when it survives in the new group (state preservation — the Inspiration
 * selection must not be lost on create↔design); otherwise the mode default.
 * @param {string|null|undefined} currentTab
 * @param {string[]} modeTabs
 * @param {string} defaultTab
 * @returns {string}
 */
export function resolveActiveTab(currentTab, modeTabs, defaultTab) {
  return currentTab && modeTabs.includes(currentTab) ? currentTab : defaultTab
}

/**
 * Register activate/deactivate hooks for a mode
 */
export function registerMode(mode, { activate, deactivate } = {}) {
  hooks[mode] = { activate, deactivate }
}

/**
 * Switch to a mode
 */
export function setMode(mode) {
  if (!MODES.includes(mode)) {
    console.warn(`[Router] Unknown mode: ${mode}`)
    return
  }

  const prev = store.getState('mode')
  if (prev === mode) return

  // Deactivate previous mode
  if (prev && hooks[prev]?.deactivate) {
    try { hooks[prev].deactivate() }
    catch (e) { console.error(`[Router] Error deactivating "${prev}":`, e) }
  }
  eventBus.emit(EVENTS.MODE_DEACTIVATED, prev)

  // Update state
  store.setState('mode', mode)

  // Toggle create mode shell escape class on body
  document.body.classList.toggle('create-mode', mode === 'create')

  // Set data-mode on .app for pillar color scoping (see tokens.css)
  const appEl = document.querySelector('.app')
  if (appEl) appEl.dataset.mode = mode

  // Update sidebar active state
  document.querySelectorAll('.rail-btn[data-mode]').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.mode === mode)
  })

  // Update the unified PageHeader in place (H-130): same DOM nodes, per-mode
  // title/subtitle/actions — the title area never re-mounts, so no jump.
  applyPageFrame(mode)

  // Toggle mode panels
  document.querySelectorAll('.mode').forEach(el => {
    el.classList.toggle('active', el.id === `mode-${mode}`)
  })

  // Update right panel tabs: show/hide mode-specific tabs
  updateRightPanelTabs(mode)

  // Activate new mode
  if (hooks[mode]?.activate) {
    try { hooks[mode].activate() }
    catch (e) { console.error(`[Router] Error activating "${mode}":`, e) }
  }
  eventBus.emit(EVENTS.MODE_ACTIVATED, mode)
  eventBus.emit(EVENTS.MODE_CHANGE, mode, prev)
}

/**
 * Update right panel tabs for the current mode
 */
function updateRightPanelTabs(mode) {
  const modeTabs = resolveModeTabs(mode)
  const defaultTab = MODE_TAB_DEFAULTS[mode] || 'community'

  // Incremental group change (H-2026-09-12-128): diff the mode's group
  // against the full tab universe — tabs in the group are revealed, the rest
  // hidden. The base group is shared by create/design, so switching between
  // them only appends/hides the design tabs; the base tabs' DOM is untouched.
  const { show, hide } = diffTabGroups(ALL_RP_TABS, modeTabs)
  document.querySelectorAll('.rp-tab[data-rp-tab]').forEach((tab) => {
    const name = tab.dataset.rpTab
    if (show.includes(name)) tab.hidden = false
    else if (hide.includes(name)) tab.hidden = true
  })

  // Keep the current tab when it survives the group change (Inspiration
  // state preserved across create↔design); only fall back to the mode
  // default when the active tab left the group.
  const currentTab = document.querySelector('.rp-tab.active')?.dataset.rpTab
  setRightPanelTab(resolveActiveTab(currentTab, modeTabs, defaultTab))
}

/**
 * Switch right panel tab
 */
export function setRightPanelTab(tabName) {
  document.querySelectorAll('.rp-tab').forEach(tab => {
    tab.classList.toggle('active', tab.dataset.rpTab === tabName)
  })
  document.querySelectorAll('.rp-panel').forEach(panel => {
    panel.classList.toggle('active', panel.dataset.rpPanel === tabName)
  })
}

/**
 * Initialize router: bind sidebar clicks, parse URL params, fire initial activate.
 *
 * Initial-activate trap (fixed 2026-05-24): if the desired initial mode
 * (URL ?mode= or store default) equals store.getState('mode'), setMode
 * early-returns at `prev === mode` and the activate hook never fires —
 * leaving the mode panel as its static placeholder. Always fire the
 * initial activate manually rather than going through setMode.
 */
export function initRouter() {
  // Sidebar mode buttons
  document.querySelectorAll('.rail-btn[data-mode]').forEach(btn => {
    btn.addEventListener('click', () => setMode(btn.dataset.mode))
  })

  // Right panel tab clicks
  document.querySelectorAll('.rp-tab[data-rp-tab]').forEach(tab => {
    tab.addEventListener('click', () => setRightPanelTab(tab.dataset.rpTab))
  })

  // Resolve desired initial mode: URL ?mode= wins, otherwise current store
  // value, otherwise 'create' default.
  const params = new URLSearchParams(window.location.search)
  const modeParam = params.get('mode')
  const desired = (modeParam && MODES.includes(modeParam))
    ? modeParam
    : (store.getState('mode') || 'create')

  // Sync store + DOM + tabs once, then always fire activate (do not
  // funnel through setMode — its prev===mode guard would skip activate).
  store.setState('mode', desired)
  // Toggle create mode shell escape class on body for initial mode
  document.body.classList.toggle('create-mode', desired === 'create')
  const appEl = document.querySelector('.app')
  if (appEl) appEl.dataset.mode = desired
  document.querySelectorAll('.rail-btn[data-mode]').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.mode === desired)
  })
  document.querySelectorAll('.mode').forEach(el => {
    el.classList.toggle('active', el.id === `mode-${desired}`)
  })
  // Unified PageHeader (H-130) — in-place title/subtitle/actions update.
  applyPageFrame(desired)
  updateRightPanelTabs(desired)

  if (hooks[desired]?.activate) {
    try { hooks[desired].activate() }
    catch (e) { console.error(`[Router] Error activating initial "${desired}":`, e) }
  }
  eventBus.emit(EVENTS.MODE_ACTIVATED, desired)
}

export default { initRouter, setMode, setRightPanelTab, registerMode }
